import api from 'services/api';
import { array, enforce, nullable, num, object, runSchema, str } from 'validation';

// Source of truth for the wire shape: nap-backend `routes/lists.js`
// GET /lists/zones (with authorized=true + resource_type + action).
// The response is `{ zones: rows }` where each row is the projection
// `(zone_id, name, description)` from the SQL handler. Only those
// three fields are surfaced because they're all an LOV needs — the
// full Zone DTO carries retention_policies / timestamps / etc that
// dropdown callers never touch.

export interface AuthorizedZone {
  readonly zone_id: number;
  readonly name: string;
  readonly description: string | null;
}

const authorizedZoneSchema = object<AuthorizedZone>({
  zone_id: num(),
  name: str(),
  description: nullable(str()),
});

interface AuthorizedZonesResponse {
  readonly zones: readonly AuthorizedZone[];
}

const authorizedZonesResponseSchema = object<AuthorizedZonesResponse>({
  zones: array(authorizedZoneSchema),
});

export type ZoneAction = 'create' | 'update';

/**
 * Curried: returns a zero-arg loader that fetches the zones the
 * current user is authorised to act on for a given (resourceType,
 * action) pair. Curried shape lets callers hand the loader straight
 * to `EditReference.load` without rewrapping.
 *
 * Backend privilege-map only recognises specific pairs; an unknown
 * one is 400-rejected — caller is responsible for passing one the
 * backend whitelists (see privilegeMap in `routes/lists.js`).
 */
export const getAuthorizedZones = (resourceType: string, action: ZoneAction) => async (): Promise<readonly AuthorizedZone[]> => {
  const res = await api.get('/lists/zones', {
    params: { authorized: 'true', resource_type: resourceType, action },
  });
  const parsed = runSchema(authorizedZonesResponseSchema, res.data, 'getAuthorizedZones');
  if (!parsed.ok) {
    throw new Error('getAuthorizedZones: response failed validation');
  }
  return enforce(parsed.value, parsed.warnings, ['zones']).zones;
};
